import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Features from '../components/Features';
import StatsSection from '../components/StatsSection';
import HowItWorks from '../components/HowItWorks';
import Testimonials from '../components/Testimonials';
import CTASection from '../components/CTASection';
import Footer from '../components/Footer';

// Import the application workflow components
import ApplicationWorkflow from '../components/ApplicationWorkflow';

const Home: React.FC = () => {
  const location = useLocation();
  const [showApplication, setShowApplication] = useState(false);
  const [prefillData, setPrefillData] = useState<any>(null);

  // Handle pre-filled data from inventory page
  useEffect(() => {
    if (location.state?.prefillData) {
      setPrefillData(location.state.prefillData);
      setShowApplication(true);
      // Clear the state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const startApplication = () => {
    setShowApplication(true);
    // Scroll to top when opening application
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToHome = () => {
    setShowApplication(false);
    // Scroll to top when going back to home
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header onGetStarted={startApplication} />
      <main className="flex-grow">
        {showApplication ? (
          <ApplicationWorkflow onBackToHome={backToHome} prefillData={prefillData} />
        ) : (
          <HeroSection onGetPreApproved={startApplication} />
        )}
        <Features />
        <StatsSection />
        <HowItWorks onStartApplication={startApplication} />
        <Testimonials />
        <CTASection onGetPreApproved={startApplication} />
      </main>
      <Footer />
      
    </div>
  );
};

// Hero Section Component
const HeroSection: React.FC<{ onGetPreApproved?: () => void }> = ({ onGetPreApproved }) => {
  return (
    <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-black opacity-10"></div>
      <div className="absolute inset-0" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 lg:pt-12 lg:pb-32">
        {/* Cash Back Promo Banner - FULL WIDTH - AT TOP */}
        <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400 border-4 border-yellow-300 rounded-2xl p-4 sm:p-6 mb-8 shadow-2xl transform hover:scale-105 transition-transform w-full">
          <div className="flex flex-col sm:flex-row items-center justify-center space-x-4 sm:space-x-5 text-center sm:text-left">
            <div className="text-6xl sm:text-7xl lg:text-8xl font-black text-blue-900 mb-1 sm:mb-0">$500</div>
            <div className="flex-1">
              <div className="font-black text-blue-900 text-3xl sm:text-4xl lg:text-5xl mb-1">CASH BACK!</div>
              <div className="font-bold text-blue-800 text-lg sm:text-xl lg:text-2xl">Get $500 CAD cash when you purchase a car from us!</div>
              <div className="text-blue-700 text-xs sm:text-sm lg:text-base mt-1">*Terms and conditions apply</div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Get the Car You Want, 
              <span className="text-yellow-400"> When You Want It</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 leading-relaxed">
              Fast, easy, and transparent car financing. Get pre-approved in minutes and drive away with confidence.
            </p>
            
            {/* Key Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-blue-100">Pre-approved in 2 minutes</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-blue-100">No hidden fees</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-blue-100">Drive today</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                <span className="text-blue-100 font-semibold">$500 CAD Cash Back*</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button 
                onClick={onGetPreApproved}
                className="bg-yellow-400 text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors shadow-lg"
              >
                Get Pre-Approved Now
              </button>
            </div>
          </div>

          {/* Right Content - Quick Start */}
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-8 border border-white border-opacity-20">
            <h3 className="text-2xl font-bold mb-6 text-center">Quick Start</h3>
            <div className="space-y-4 text-center">
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-yellow-400 text-blue-900 rounded-full flex items-center justify-center font-bold">1</div>
                <span className="text-blue-100">Click "Get Pre-Approved Now"</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-yellow-400 text-blue-900 rounded-full flex items-center justify-center font-bold">2</div>
                <span className="text-blue-100">Answer a few quick questions</span>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <div className="w-8 h-8 bg-yellow-400 text-blue-900 rounded-full flex items-center justify-center font-bold">3</div>
                <span className="text-blue-100">Get your pre-approval instantly</span>
              </div>
            </div>
            <button
              onClick={onGetPreApproved}
              className="w-full bg-yellow-400 text-blue-900 py-3 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-colors mt-6"
            >
              Start Application
            </button>
            <p className="text-blue-200 text-sm text-center mt-4">
              * Takes less than 2 minutes
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;
